import React, { useEffect, useMemo, useState } from "react";
import { fmtTime, fmtRange } from "@/lib/enrol";
import { api, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { DollarSign, ChevronDown, ChevronRight, CheckCircle2, Clock, Wallet, Plus, X, Pencil, Trash2, Send, FileDown, AlertTriangle, Mail } from "lucide-react";
import { useConfirm } from "@/lib/confirm";

const KIND_LABEL = { class: "Class", class_cover: "Cover", workshop: "Workshop", event: "Event" };
const KIND_COLOR = { class: "text-red-400", class_cover: "text-amber-400", workshop: "text-emerald-400", event: "text-sky-400" };
const STATUS_COLOR = { pending: "border-zinc-500/40 text-zinc-400", verified: "border-amber-500/40 text-amber-400", paid: "border-emerald-500/40 text-emerald-400" };

export default function Payroll() {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || user?.role === "super_admin";
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [logOpen, setLogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payroll");
      setSummary(r.data);
    } catch (e) { toast.error("Failed to load payroll"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const verify = async (rid) => {
    try { await api.post(`/admin/attendance/${rid}/verify`); toast.success("Verified"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const verifyAll = async (uid, name, pendingCount, pendingAmount) => {
    if (!pendingCount) { toast.error("No pending records to verify"); return; }
    const ok = await confirm({
      title: `Verify all ${pendingCount} pending record(s) for ${name}?`,
      description: `Every pending line ($${(pendingAmount || 0).toFixed(2)}) will be moved to Verified so it can be paid out.`,
      confirmLabel: "Verify all",
    });
    if (!ok) return;
    try {
      const r = await api.post(`/admin/payroll/${uid}/verify-all`);
      toast.success(`Verified ${r.data.verified} record(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (rid) => {
    const ok = await confirm({ title: "Delete this record?", description: "Removes the line from payroll.", danger: true, confirmLabel: "Delete" });
    if (!ok) return;
    try { await api.delete(`/admin/attendance/${rid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const downloadPayslip = async (uid, name) => {
    try {
      const r = await api.get(`/admin/payroll/${uid}/payslip.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `payslip-${(name || uid).toLowerCase().replace(/\s+/g,"-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate payslip"); }
  };
  const emailPayslip = async (uid, name) => {
    try {
      const r = await api.post(`/admin/payroll/${uid}/send-payslip`);
      toast.success(`Payslip emailed to ${r.data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
  };
  const markPaid = async (uid, name, amountVerified) => {
    if (!amountVerified || amountVerified <= 0) { toast.error("Nothing to mark paid — verify records first"); return; }
    const ok = await confirm({
      title: `Mark $${amountVerified.toFixed(2)} paid to ${name}?`,
      description: "Every VERIFIED record is archived, this line resets, and a payslip email is auto-sent to their recovery email.",
      confirmLabel: "Mark as paid", danger: false,
    });
    if (!ok) return;
    try {
      const r = await api.post(`/admin/payroll/${uid}/mark-paid`);
      const sent = r.data?.payslip?.sent_to;
      toast.success(`Marked ${r.data.marked_paid} record(s) as paid${sent ? ` · payslip sent to ${sent}` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-4 sm:p-8 text-zinc-500">Loading payroll…</div>;

  const rows = summary?.instructors || [];
  const grandTotal = summary?.grand_total || 0;

  return (
    <div className="p-4 sm:p-8 max-w-[1600px]">
      <div className="flex justify-between items-end mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2"><Wallet size={12}/>Payroll</div>
          <h1 className="font-display text-5xl uppercase">Instructor Pay</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">Every class, cover shift, workshop, and event an instructor conducts becomes a payroll line. Once verified, hit "Mark paid" to clear their balance.</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Outstanding total</div>
          <div className="font-display text-5xl text-white" data-testid="grand-total">${grandTotal.toFixed(2)}</div>
          <button onClick={() => setLogOpen(true)} className="btn-red !py-2 !px-3 !text-xs mt-3 flex items-center gap-1 ml-auto" data-testid="log-shift-btn">
            <Plus size={12}/>{user?.role === "instructor" ? "Log cover shift" : "Log class / cover"}
          </button>
        </div>
      </div>

      {rows.length === 0 && <div className="card-surface p-16 text-center text-zinc-500">No payroll records yet.</div>}

      <div className="space-y-3">
        {rows.map(g => {
          const isOpen = !!expanded[g.instructor_admin_id];
          return (
            <div key={g.instructor_admin_id} className="card-surface" data-testid={`instructor-row-${g.instructor_admin_id}`}>
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(e => ({ ...e, [g.instructor_admin_id]: !isOpen }))}>
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                  <div>
                    <div className="text-white font-bold">{g.instructor_display || "Unassigned"}</div>
                    <div className="text-zinc-500 text-xs">
                      {Object.entries(g.counts).filter(([_,n]) => n > 0).map(([k,n]) => `${n} ${KIND_LABEL[k] || k}${n>1?"s":""}`).join(" · ") || "no records"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Pending / Verified</div>
                    <div className="text-sm">
                      <span className="text-zinc-400">${g.total_pending.toFixed(2)}</span>
                      <span className="text-zinc-600 mx-2">/</span>
                      <span className="text-amber-400 font-bold">${g.total_verified.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="text-right min-w-[120px]">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Grand total</div>
                    <div className="text-2xl font-display text-white" data-testid={`grand-${g.instructor_admin_id}`}>${g.grand_total.toFixed(2)}</div>
                  </div>
                  {isManager && (
                    <button
                      onClick={e => { e.stopPropagation(); const pc = (g.records || []).filter(r => r.status === "pending").length; verifyAll(g.instructor_admin_id, g.instructor_display, pc, g.total_pending); }}
                      disabled={g.total_pending <= 0}
                      className="btn-ghost !py-2 !px-3 !text-xs disabled:opacity-30 flex items-center gap-1"
                      title="Verify every pending record for this instructor"
                      data-testid={`verify-all-${g.instructor_admin_id}`}
                    >
                      <CheckCircle2 size={12}/>Verify all
                    </button>
                  )}
                  {isManager && (
                    <button
                      onClick={e => { e.stopPropagation(); markPaid(g.instructor_admin_id, g.instructor_display, g.total_verified); }}
                      disabled={g.total_verified <= 0}
                      className="btn-red !py-2 !px-3 !text-xs disabled:opacity-30 flex items-center gap-1"
                      data-testid={`mark-paid-${g.instructor_admin_id}`}
                    >
                      <Send size={12}/>Mark ${g.total_verified.toFixed(2)} paid
                    </button>
                  )}
                  <button
                    onClick={e => { e.stopPropagation(); emailPayslip(g.instructor_admin_id, g.instructor_display); }}
                    className="btn-ghost !py-2 !px-3 !text-xs flex items-center gap-1"
                    title="Email payslip to recovery email"
                    data-testid={`email-payslip-${g.instructor_admin_id}`}
                  >
                    <Mail size={12}/>Email
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); downloadPayslip(g.instructor_admin_id, g.instructor_display); }}
                    className="btn-ghost !py-2 !px-3 !text-xs flex items-center gap-1"
                    data-testid={`payslip-${g.instructor_admin_id}`}
                  >
                    <FileDown size={12}/>PDF
                  </button>
                </div>
              </div>
              {isOpen && (
                <div className="border-t border-white/10">
                  {isManager && (
                    <div className="px-4 py-3 border-b border-white/5 bg-black/40 flex items-center gap-6 text-xs flex-wrap" data-testid={`bank-${g.instructor_admin_id}`}>
                      <div className="eyebrow flex items-center gap-1"><Wallet size={10}/>Pay to</div>
                      {g.has_bank_details ? (
                        <>
                          {g.bank_account_name && <div><span className="text-zinc-500">Name:</span> <span className="text-white font-mono">{g.bank_account_name}</span></div>}
                          {g.bank_bsb && <div><span className="text-zinc-500">BSB:</span> <span className="text-white font-mono">{g.bank_bsb}</span></div>}
                          {g.bank_account_number && <div><span className="text-zinc-500">Acc:</span> <span className="text-white font-mono">{g.bank_account_number}</span></div>}
                          {g.pay_id && <div><span className="text-zinc-500">PayID:</span> <span className="text-white font-mono">{g.pay_id}</span></div>}
                        </>
                      ) : (
                        <div className="text-amber-400 flex items-center gap-1"><AlertTriangle size={12}/>No bank / PayID on file — ask them to fill it in from Sidebar → My account</div>
                      )}
                    </div>
                  )}
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-zinc-500 uppercase tracking-widest text-[10px]">
                        <th className="text-left px-4 py-3">Date</th>
                        <th className="text-left px-4 py-3">Kind</th>
                        <th className="text-left px-4 py-3">Program</th>
                        <th className="text-left px-4 py-3">Covered for</th>
                        <th className="text-left px-4 py-3">Pay</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.records.map(r => (
                        <tr key={r.id} className="border-t border-white/5" data-testid={`record-${r.id}`}>
                          <td className="px-4 py-3 text-xs text-zinc-400">{r.date}</td>
                          <td className={`px-4 py-3 text-xs uppercase tracking-widest ${KIND_COLOR[r.kind] || ""}`}>{KIND_LABEL[r.kind] || r.kind}</td>
                          <td className="px-4 py-3">
                            <div className="text-white">{r.program_snapshot?.name || "—"}</div>
                            <div className="text-zinc-500 text-[10px]">{r.program_snapshot?.detail}</div>
                            {r.notes && <div className="text-zinc-500 text-[10px] mt-1 italic">{r.notes}</div>}
                          </td>
                          <td className="px-4 py-3 text-xs text-zinc-400">{r.covered_for_display || "—"}</td>
                          <td className="px-4 py-3 text-white">${Number(r.pay_amount ?? 0).toFixed(2)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_COLOR[r.status] || ""}`}>{r.status}</span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {isManager && r.status === "pending" && (
                              <button onClick={() => verify(r.id)} className="text-emerald-400 hover:text-emerald-300 p-1" title="Verify" data-testid={`verify-${r.id}`}><CheckCircle2 size={14}/></button>
                            )}
                            {isManager && (
                              <button onClick={() => setEditing(r)} className="text-zinc-400 hover:text-white p-1 ml-1" title="Edit" data-testid={`edit-${r.id}`}><Pencil size={14}/></button>
                            )}
                            {isManager && (
                              <button onClick={() => del(r.id)} className="text-zinc-400 hover:text-red-500 p-1 ml-1" title="Delete" data-testid={`del-${r.id}`}><Trash2 size={14}/></button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {g.records.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500">No records for this instructor.</td></tr>}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {logOpen && <LogShiftModal onClose={() => setLogOpen(false)} onDone={() => { setLogOpen(false); load(); }} me={user}/>}
      {editing && <EditRecordModal record={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function LogShiftModal({ onClose, onDone, me }) {
  const isInstructor = me?.role === "instructor";
  const [kind, setKind] = useState(isInstructor ? "class_cover" : "class");
  const [sessions, setSessions] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [events, setEvents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [form, setForm] = useState({
    program_id: "",
    instructor_admin_id: isInstructor ? me.id : "",
    covered_for_admin_id: "",
    date: new Date().toISOString().slice(0,10),
    pay_amount: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/sessions").catch(() => ({ data: [] })),
      api.get("/admin/workshops").catch(() => ({ data: [] })),
      api.get("/admin/events").catch(() => ({ data: [] })),
      api.get("/admin/admin-users").catch(() => ({ data: [] })),
    ]).then(([s, w, e, u]) => {
      setSessions(s.data || []);
      setWorkshops(w.data || []);
      setEvents(e.data || []);
      setInstructors((u.data || []).filter(x => x.role === "instructor"));
    });
  }, []);

  // Auto-fill pay when program picked
  useEffect(() => {
    if (!form.program_id) return;
    let src = null;
    if (kind === "class" || kind === "class_cover") src = sessions.find(x => x.id === form.program_id);
    else if (kind === "workshop") src = workshops.find(x => x.id === form.program_id);
    else if (kind === "event") src = events.find(x => x.id === form.program_id);
    if (src?.pay_amount != null && form.pay_amount === "") setForm(f => ({ ...f, pay_amount: src.pay_amount }));
    // eslint-disable-next-line
  }, [form.program_id, kind]);

  const options = useMemo(() => {
    if (kind === "class" || kind === "class_cover") return sessions.map(s => ({ id: s.id, label: `${s.batch_name || "Class"} · ${s.day} ${fmtTime(s.start_time)} · ${s.location_name || "—"}`, pay: s.pay_amount }));
    if (kind === "workshop") return workshops.map(w => ({ id: w.id, label: `${w.name} · ${w.date} · ${w.location_name || ""}`, pay: w.pay_amount }));
    if (kind === "event") return events.map(e => ({ id: e.id, label: `${e.caption} · ${e.date}`, pay: e.pay_amount }));
    return [];
  }, [kind, sessions, workshops, events]);

  const coveredForOptions = useMemo(
    () => instructors.filter(u => u.id !== form.instructor_admin_id),
    [instructors, form.instructor_admin_id],
  );

  const submit = async () => {
    if (!form.program_id) { toast.error("Pick a program"); return; }
    if (!form.instructor_admin_id) { toast.error("Pick the instructor"); return; }
    if (kind === "class_cover" && !form.covered_for_admin_id) { toast.error("Pick who they're covering"); return; }
    setSaving(true);
    try {
      const payload = {
        kind, program_id: form.program_id, instructor_admin_id: form.instructor_admin_id,
        covered_for_admin_id: kind === "class_cover" ? form.covered_for_admin_id : null,
        date: form.date, notes: form.notes || null,
        pay_amount: form.pay_amount === "" ? null : Number(form.pay_amount),
      };
      await api.post("/admin/attendance", payload);
      toast.success("Shift logged");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="card-surface p-6 max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl uppercase">Log shift</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="eyebrow block mb-1">Kind</label>
            <select value={kind} onChange={e => { setKind(e.target.value); setForm(f => ({...f, program_id: "", pay_amount: ""})); }} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" disabled={isInstructor} data-testid="log-kind">
              {isInstructor ? (
                <option value="class_cover">Cover shift (covering someone else)</option>
              ) : (
                <>
                  <option value="class">Class (taught by assigned instructor)</option>
                  <option value="class_cover">Cover shift</option>
                  <option value="workshop">Workshop</option>
                  <option value="event">Event</option>
                </>
              )}
            </select>
          </div>
          <div className="col-span-2">
            <label className="eyebrow block mb-1">Program</label>
            <select value={form.program_id} onChange={e => setForm({...form, program_id: e.target.value, pay_amount: ""})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="log-program">
              <option value="">—</option>
              {options.map(o => <option key={o.id} value={o.id}>{`${o.label}${o.pay ? " · default $" + o.pay : ""}`}</option>)}
            </select>
          </div>
          {!isInstructor && (
            <div className="col-span-2">
              <label className="eyebrow block mb-1">Instructor conducted this</label>
              <select value={form.instructor_admin_id} onChange={e => setForm({...form, instructor_admin_id: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="log-instructor">
                <option value="">—</option>
                {instructors.map(u => <option key={u.id} value={u.id}>{`${u.instructor_name || u.name} · ${u.email}`}</option>)}
              </select>
            </div>
          )}
          {kind === "class_cover" && (
            <div className="col-span-2">
              <label className="eyebrow block mb-1">Covered for</label>
              <select value={form.covered_for_admin_id} onChange={e => setForm({...form, covered_for_admin_id: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="log-covered-for">
                <option value="">—</option>
                {coveredForOptions.map(u => <option key={u.id} value={u.id}>{`${u.instructor_name || u.name} · ${u.email}`}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="eyebrow block mb-1">Date</label>
            <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="log-date"/>
          </div>
          <div>
            <label className="eyebrow block mb-1">Pay ($)</label>
            <input type="number" step="0.01" value={form.pay_amount} onChange={e => setForm({...form, pay_amount: e.target.value})} placeholder="Auto-filled from program" className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="log-pay"/>
          </div>
          <div className="col-span-2">
            <label className="eyebrow block mb-1">Notes (optional)</label>
            <input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="log-notes"/>
          </div>
        </div>
        <button onClick={submit} disabled={saving} className="btn-red w-full mt-4" data-testid="log-submit">{saving ? "Saving…" : "Log shift"}</button>
      </div>
    </div>
  );
}

function EditRecordModal({ record, onClose, onDone }) {
  const [form, setForm] = useState({
    pay_amount: record.pay_amount ?? "",
    date: record.date || "",
    notes: record.notes || "",
    status: record.status || "pending",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/attendance/${record.id}`, {
        pay_amount: form.pay_amount === "" ? null : Number(form.pay_amount),
        date: form.date, notes: form.notes, status: form.status,
      });
      toast.success("Saved"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6" onClick={onClose}>
      <div className="card-surface p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl uppercase">Edit record</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="text-zinc-400 text-xs mb-3">{record.program_snapshot?.name} · {record.program_snapshot?.detail}</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="eyebrow block mb-1">Date</label>
            <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="edit-date"/>
          </div>
          <div>
            <label className="eyebrow block mb-1">Pay ($)</label>
            <input type="number" step="0.01" value={form.pay_amount} onChange={e => setForm({...form, pay_amount: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="edit-pay"/>
          </div>
          <div className="col-span-2">
            <label className="eyebrow block mb-1">Status</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="edit-status">
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="eyebrow block mb-1">Notes</label>
            <input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="edit-notes"/>
          </div>
        </div>
        <button onClick={submit} disabled={saving} className="btn-red w-full mt-4" data-testid="edit-submit">{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}
